import { useState, useCallback, useMemo, useEffect } from 'react'
import { format } from 'date-fns'
import { DateRange } from '@mui/x-date-pickers-pro'
import debounce from 'lodash.debounce'
import { useAppDispatch } from '../../../redux/store'
import { showErrorMessage } from '../../../redux/reducers/snackbarReducer'
import { manualRequestsService } from '../../../services/myActionsService'
import { DailyOSPDictionary } from '../../../types/absence'
import { isValidPlannerDate } from '../../../utils/date-utils'
import { INPUT_CHANGE_API_DEBOUNCE_DELAY } from '../../../utils/constants'
import { AbsenceTypesEnum } from '../../Absence/types'

export interface UseOSPCalculationsParams {
  employeeId?: number
  dateRange: DateRange<Date> | [Date | null, Date | null]
  selectedDays?: { day: Date | string; hours: number; checked: boolean }[] | null
  absenceCategoryId?: number
}

export interface UseOSPCalculationsReturn {
  // State
  remainingOSP: number | null
  totalSickPayAllowanceHours: number | null
  isLoadingOSP: boolean
  dailyOSP: DailyOSPDictionary

  // Computed values
  totalAvailableOSP: number
  totalRequestedHours: number
  exceededHours: number
  exceededHoursPerDay: Record<string, number>

  // Methods
  fetchRemainingOSP: () => Promise<void>
}

/**
 * Custom hook to manage OSP (Occupational Sick Pay) calculations and state
 * Handles fetching per-day OSP values and computing total available OSP,
 * exceeded hours, and total requested hours
 */
export const useOSPCalculations = ({
                                     employeeId,
                                     dateRange,
                                     selectedDays,
                                     absenceCategoryId,
                                   }: UseOSPCalculationsParams): UseOSPCalculationsReturn => {
  const dispatch = useAppDispatch()

  const [remainingOSP, setRemainingOSP] = useState<number | null>(null)
  const [totalSickPayAllowanceHours, setTotalSickPayAllowanceHours] = useState<number | null>(null)
  const [isLoadingOSP, setIsLoadingOSP] = useState<boolean>(false)
  const [dailyOSP, setDailyOSP] = useState<DailyOSPDictionary>({})

  /**
   * Fetches OSP allowance for the date range
   * Only applicable for sickness absences
   */
  const fetchRemainingOSP = useCallback(async () => {
    if (!employeeId || !isValidPlannerDate(dateRange[0]) || !isValidPlannerDate(dateRange[1])) return

    setIsLoadingOSP(true)
    try {
      const response = await manualRequestsService.getRemainingOSPAllowance(
        employeeId,
        dateRange[0],
        dateRange[1]
      )

      // Store per-day OSP dictionary for sickness absences
      setDailyOSP(response.dailyOSP || {})

      // Get first day OSP for backward compatibility with UI display
      const firstDate = format(dateRange[0], 'yyyy-MM-dd')
      setRemainingOSP(response.dailyOSP?.[firstDate] ?? null)
      setTotalSickPayAllowanceHours(response.totalSickPayAllowanceHours)
    } catch (error) {
      dispatch(showErrorMessage('Failed to get remaining OSP allowance'))
      setRemainingOSP(null)
      setDailyOSP({})
    } finally {
      setIsLoadingOSP(false)
    }
  }, [dispatch, employeeId, dateRange])

  // Debounce is used here to prevent the premature API requests before user has finished typing the dates
  const debouncedFetchRemainingOSP = useMemo(
    () => debounce(fetchRemainingOSP, INPUT_CHANGE_API_DEBOUNCE_DELAY),
    [fetchRemainingOSP]
  )

  /**
   * Auto-fetch OSP when employee or date range changes
   * Only fetch for sickness absences - OSP doesn't apply to non-sickness absences
   */
  useEffect(() => {
    // Don't fetch OSP for non-sickness absences
    if (absenceCategoryId === AbsenceTypesEnum.NonSickness) {
      return
    }

    if (employeeId && isValidPlannerDate(dateRange[0]) && isValidPlannerDate(dateRange[1])) {
      debouncedFetchRemainingOSP()
    }

    return () => {
      debouncedFetchRemainingOSP.cancel()
    }

  }, [employeeId, dateRange[0], dateRange[1], absenceCategoryId, debouncedFetchRemainingOSP])

  /**
   * Calculate total requested hours from selected days
   */
  const totalRequestedHours = useMemo(() => {
    if (!selectedDays || selectedDays.length === 0) return 0
    return selectedDays.reduce((total, day) => total + (day.checked ? day.hours : 0), 0)
  }, [selectedDays])

  /**
   * Calculate total available OSP across all dates in the range
   * OSP values from API are cumulative, so we take the maximum value
   * to know the total OSP available for the entire absence period
   * This is used for display purposes in the UI
   */
  const totalAvailableOSP = useMemo(() => {
    if (Object.keys(dailyOSP).length === 0) return 0

    // Find the maximum cumulative OSP across ALL days (not just checked)
    const ospValues = Object.values(dailyOSP)
    const maxCumulativeOSP = Math.max(...ospValues, 0)

    return maxCumulativeOSP
  }, [dailyOSP])

  /**
   * Calculate how many hours exceed the available OSP, both total and per-day
   *
   * For non-sickness absences: All hours are considered "exceeded" (requiring discretionary pay)
   * For sickness absences: Calculate based on OSP availability using a running total
   *
   * The API returns OSP available on each date WITHOUT considering the absence being created.
   * We need to track OSP consumption as a running total through the checked days.
   *
   * For example, if API returns 32 hours available on each day:
   * - Day 1: 32 available, use 8 → 24 remaining, 0 exceeded
   * - Day 2: API says 32, but we have 24 from Day 1, use 8 → 16 remaining, 0 exceeded
   * - Day 3: API says 32, but we have 16 from Day 2, use 8 → 8 remaining, 0 exceeded
   *
   * Returns both the total exceeded hours and a per-day breakdown
   */
  const { exceededHours, exceededHoursPerDay } = useMemo(() => {
    if (!selectedDays || selectedDays.length === 0) {
      return { exceededHours: 0, exceededHoursPerDay: {} }
    }

    // For non-sickness absences, all hours are "exceeded" (require discretionary pay)
    // OSP (Occupational Sick Pay) doesn't apply to non-sickness absences
    if (absenceCategoryId === AbsenceTypesEnum.NonSickness) {
      const perDayExceeded: Record<string, number> = {}
      let total = 0

      selectedDays
        .filter(day => day.checked)
        .forEach(day => {
          const dateStr = typeof day.day === 'string' ? day.day : format(day.day, 'yyyy-MM-dd')
          perDayExceeded[dateStr] = day.hours
          total += day.hours
        })

      return { exceededHours: total, exceededHoursPerDay: perDayExceeded }
    }

    // For sickness absences, calculate based on OSP availability
    if (Object.keys(dailyOSP).length === 0) {
      // No OSP data available - all hours require discretionary pay
      const perDayExceeded: Record<string, number> = {}
      let total = 0

      selectedDays
        .filter(day => day.checked)
        .forEach(day => {
          const dateStr = typeof day.day === 'string' ? day.day : format(day.day, 'yyyy-MM-dd')
          perDayExceeded[dateStr] = day.hours
          total += day.hours
        })

      return { exceededHours: total, exceededHoursPerDay: perDayExceeded }
    }

    // Helper function to get OSP available on a specific date from the dictionary (O(1) lookup)
    const getOSPForDate = (date: Date | string): number => {
      const dateStr = typeof date === 'string' ? date : format(date, 'yyyy-MM-dd')
      return dailyOSP[dateStr] ?? 0
    }

    // Sort checked days chronologically
    const sortedCheckedDays = [...selectedDays]
      .filter(day => day.checked)
      .sort((a, b) => {
        const dateA = typeof a.day === 'string' ? new Date(a.day) : a.day
        const dateB = typeof b.day === 'string' ? new Date(b.day) : b.day
        return dateA.getTime() - dateB.getTime()
      })

    // Track OSP as a running total through the checked days
    let ospRemaining = 0
    let totalExceeded = 0
    let isFirstCheckedDay = true
    let previousDayOSPFromAPI = 0
    const perDayExceeded: Record<string, number> = {}

    for (const day of sortedCheckedDays) {
      // Get OSP available on this date from the API (doesn't account for this absence)
      const ospAvailableOnThisDate = getOSPForDate(day.day)

      if (isFirstCheckedDay) {
        // First checked day: start with the OSP available on this date
        ospRemaining = ospAvailableOnThisDate
        isFirstCheckedDay = false
      } else if (ospAvailableOnThisDate > previousDayOSPFromAPI) {
        // Subsequent days: only update if MORE OSP became available compared to previous day's API value
        // This handles cases where old absences roll out of the 52-week window
        const newOSP = ospAvailableOnThisDate - previousDayOSPFromAPI
        ospRemaining += newOSP
      }

      // Track this day's API value for next iteration
      previousDayOSPFromAPI = ospAvailableOnThisDate

      // Calculate if this day exceeds available OSP
      const dateStr = typeof day.day === 'string' ? day.day : format(day.day, 'yyyy-MM-dd')

      if (day.hours > ospRemaining) {
        // This day exceeds OSP
        const exceeded = day.hours - ospRemaining
        totalExceeded += exceeded
        perDayExceeded[dateStr] = exceeded
        ospRemaining = 0
      } else {
        // This day is covered by OSP
        perDayExceeded[dateStr] = 0
        ospRemaining -= day.hours
      }
    }

    return { exceededHours: totalExceeded, exceededHoursPerDay: perDayExceeded }
  }, [dailyOSP, selectedDays, absenceCategoryId])

  return {
    // State
    remainingOSP,
    totalSickPayAllowanceHours,
    isLoadingOSP,
    dailyOSP,

    // Computed values
    totalAvailableOSP,
    totalRequestedHours,
    exceededHours,
    exceededHoursPerDay,

    // Methods
    fetchRemainingOSP,
  }
}